// src/utils/personalStatementDocxGenerator.js
import DOMPurify from "dompurify";

export const generateAndDownloadPersonalStatementDocx = ({
  personalStatementAnswers,
  selectedPSSubject,
  selectedGuidance,
}) => {
  if (!selectedPSSubject) return;

  const currentAnswers = personalStatementAnswers[selectedPSSubject] || {};

  const ucasQuestions = [
    "Why do you want to study this course or subject?",
    "How have your qualifications and studies helped you to prepare for this course or subject?",
    "What else supports your application?",
  ];

  const guidances = selectedGuidance?.guidances || [];
  const tutorInsights = selectedGuidance?.tutor_insights || "";

  const escapeXml = (str = "") =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;")
      .replace(/[\x00-\x08\x0B\f\x0E-\x1F]/g, ""); // ← critical: remove control chars

  const stripHtml = (html = "") => {
    if (!html) return "";
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(?:p|div|li|h[1-6])>/gi, "\n")
      .replace(/<li>/gi, "• ")
      .replace(/<[^>]+>/g, "")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const multiLineParagraphs = ({ text = "", size = 22, color = "000000", italic = false, afterLast = 120 } = {}) => {
    const lines = stripHtml(text).split("\n");
    return lines
      .map((line, i) => {
        const spacing = i === lines.length - 1 ? afterLast : 0;
        let rPr = italic ? "<w:i/>" : "";
        rPr += `<w:color w:val="${color}"/><w:sz w:val="${size}"/><w:szCs w:val="${size}"/>`;
        return `<w:p><w:pPr><w:spacing w:after="${spacing}"/></w:pPr><w:r><w:rPr>${rPr}</w:rPr><w:t xml:space="preserve">${escapeXml(
          line || " "
        )}</w:t></w:r></w:p>`;
      })
      .join("");
  };

  let bodyXml = "";

  // ── Your original content (unchanged) ──────────────────────────────────────
  bodyXml += `<w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="120"/></w:pPr><w:r><w:rPr><w:b/><w:color w:val="1F3864"/><w:sz w:val="40"/><w:szCs w:val="40"/></w:rPr><w:t>University Application Essays</w:t></w:r></w:p>`;

  if (selectedPSSubject) {
    bodyXml += `<w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="60"/></w:pPr><w:r><w:rPr><w:b/><w:color w:val="2E75B6"/><w:sz w:val="28"/><w:szCs w:val="28"/></w:rPr><w:t>Subject: ${escapeXml(selectedPSSubject)}</w:t></w:r></w:p>`;
  }

  const dateStr = new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
  bodyXml += `<w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="480"/></w:pPr><w:r><w:rPr><w:i/><w:color w:val="666666"/><w:sz w:val="20"/><w:szCs w:val="20"/></w:rPr><w:t>Generated: ${escapeXml(dateStr)}</w:t></w:r></w:p>`;

  bodyXml += `<w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="2E75B6"/></w:pBdr><w:spacing w:after="400"/></w:pPr></w:p>`;

  if (tutorInsights?.trim()) {
    bodyXml += `<w:p><w:pPr><w:spacing w:before="200" w:after="160"/></w:pPr><w:r><w:rPr><w:b/><w:color w:val="1F3864"/><w:sz w:val="28"/><w:szCs w:val="28"/></w:rPr><w:t>Admissions Tutor Insights</w:t></w:r></w:p>`;

    const insightLines = stripHtml(tutorInsights).split("\n").filter(l => l.trim());
    insightLines.forEach((line, i) => {
      bodyXml += `<w:p><w:pPr><w:pBdr><w:left w:val="single" w:sz="8" w:space="4" w:color="2E75B6"/></w:pBdr><w:spacing w:after="${i === insightLines.length - 1 ? 0 : 80}" w:before="0"/><w:ind w:left="240"/></w:pPr><w:r><w:rPr><w:color w:val="1a1a2e"/><w:sz w:val="22"/><w:szCs w:val="22"/></w:rPr><w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r></w:p>`;
    });

    bodyXml += `<w:p><w:pPr><w:spacing w:after="480"/></w:pPr></w:p>`;
    bodyXml += `<w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="4" w:space="1" w:color="CCCCCC"/></w:pBdr><w:spacing w:after="480"/></w:pPr></w:p>`;
  }

  ucasQuestions.forEach((question, index) => {
    const answer = currentAnswers[index] || "";
    const guidanceItem = guidances.find((g) => g.order === index + 1);
    const guidanceText = guidanceItem?.guidance_text || "";

    bodyXml += `<w:p><w:pPr><w:spacing w:before="360" w:after="80"/></w:pPr><w:r><w:rPr><w:b/><w:color w:val="2E75B6"/><w:sz w:val="22"/><w:szCs w:val="22"/><w:caps/></w:rPr><w:t>Question ${index + 1}</w:t></w:r></w:p>`;

    bodyXml += `<w:p><w:pPr><w:spacing w:after="200"/></w:pPr><w:r><w:rPr><w:b/><w:color w:val="1F3864"/><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr><w:t xml:space="preserve">${escapeXml(question)}</w:t></w:r></w:p>`;

    if (guidanceText?.trim()) {
      bodyXml += `<w:p><w:pPr><w:spacing w:before="40" w:after="80"/></w:pPr><w:r><w:rPr><w:b/><w:i/><w:color w:val="595959"/><w:sz w:val="20"/><w:szCs w:val="20"/></w:rPr><w:t>Guidance:</w:t></w:r></w:p>`;

      const gLines = stripHtml(guidanceText).split("\n").filter(l => l.trim());
      gLines.forEach((line, i) => {
        bodyXml += `<w:p><w:pPr><w:pBdr><w:left w:val="single" w:sz="6" w:space="4" w:color="36a7f8"/></w:pBdr><w:spacing w:after="${i === gLines.length - 1 ? 0 : 60}" w:before="0"/><w:ind w:left="200"/></w:pPr><w:r><w:rPr><w:i/><w:color w:val="374151"/><w:sz w:val="20"/><w:szCs w:val="20"/></w:rPr><w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r></w:p>`;
      });

      bodyXml += `<w:p><w:pPr><w:spacing w:after="200"/></w:pPr></w:p>`;
    }

    bodyXml += `<w:p><w:pPr><w:spacing w:after="80"/></w:pPr><w:r><w:rPr><w:b/><w:i/><w:color w:val="595959"/><w:sz w:val="20"/><w:szCs w:val="20"/></w:rPr><w:t>Your Answer:</w:t></w:r></w:p>`;

    if (answer.trim()) {
      answer.split("\n").forEach((line, lineIdx, arr) => {
        bodyXml += `<w:p><w:pPr><w:spacing w:after="${lineIdx === arr.length - 1 ? 120 : 0}"/></w:pPr><w:r><w:rPr><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr><w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r></w:p>`;
      });
    } else {
      bodyXml += `<w:p><w:pPr><w:spacing w:after="120"/></w:pPr><w:r><w:rPr><w:i/><w:color w:val="999999"/><w:sz w:val="22"/><w:szCs w:val="22"/></w:rPr><w:t>(Not yet answered)</w:t></w:r></w:p>`;
    }

    bodyXml += `<w:p><w:pPr><w:spacing w:after="320"/></w:pPr><w:r><w:rPr><w:i/><w:color w:val="888888"/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Character count: ${answer.length}</w:t></w:r></w:p>`;

    if (index < ucasQuestions.length - 1) {
      bodyXml += `<w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="2" w:space="1" w:color="CCCCCC"/></w:pBdr><w:spacing w:after="320"/></w:pPr></w:p>`;
    }
  });

  bodyXml += `<w:p><w:pPr><w:pBdr><w:top w:val="single" w:sz="6" w:space="1" w:color="2E75B6"/></w:pBdr><w:jc w:val="center"/><w:spacing w:before="480" w:after="160"/></w:pPr><w:r><w:rPr><w:i/><w:color w:val="555555"/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Generated by Oxbridge Gateway ASPIREdeck — All drafts saved locally in your browser.</w:t></w:r></w:p>`;

  // ── Fixed & complete OOXML ─────────────────────────────────────────────────
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${bodyXml}
    <w:sectPr>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/>
    </w:sectPr>
  </w:body>
</w:document>`;

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
</Types>`;

  const rootRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

  const docRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults>
    <w:rPrDefault>
      <w:rPr>
        <w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>
        <w:sz w:val="22"/>
      </w:rPr>
    </w:rPrDefault>
  </w:docDefaults>
</w:styles>`;

  // ── Download ───────────────────────────────────────────────────────────────
  const filename = `UniStatement_${selectedPSSubject.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.docx`;

  const buildAndDownload = (JSZip) => {
    const zip = new JSZip();
    zip.file("[Content_Types].xml", contentTypesXml);
    zip.file("_rels/.rels", rootRelsXml);
    zip.file("word/document.xml", documentXml);
    zip.file("word/_rels/document.xml.rels", docRelsXml);
    zip.file("word/styles.xml", stylesXml);

    zip.generateAsync({ type: "blob" })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("ZIP generation failed:", err);
        alert("Failed to generate Word document.");
      });
  };

  if (window.JSZip) {
    buildAndDownload(window.JSZip);
  } else {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    script.onload = () => buildAndDownload(window.JSZip);
    script.onerror = () => alert("Failed to load ZIP library. Check internet.");
    document.head.appendChild(script);
  }
};